import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import React from "react";
import { useIntl } from "react-intl";
import { useGetAllTrainingProvidersQuery } from "../../../../api/rtk/TrainingProviderApi";
import { ModuleSelectionDetails } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import { ModuleTypeTranslationKey } from "../../../../model/Module";
import { OralExamOption } from "../../../../model/OralExamOption";
import { GetTranslatedValueIntl } from "../../../../model/Shared/TranslatedValue";
import { GetTranslation } from "../../../../translations/GetTranslation";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import ModuleAttachments from "./ModuleAttachments";
import { ModuleData } from "./ModulesDetailsStep";

const useStyles = makeStyles({
  table: {
    "& td": {
      borderBottom: "none",
    },
    "& td:nth-child(1)": {
      width: "150px",
    },
  },
});

type ModuleDetailsDialogProps = {
  moduleData: ModuleData;
  handleOk: (details: ModuleSelectionDetails) => void;
  handleCancel: () => void;
};

const ModuleDetailsDialog: React.FunctionComponent<ModuleDetailsDialogProps> = ({
  moduleData,
  handleOk,
  handleCancel,
}) => {
  const [trainingProvider, setTrainingProvider] = React.useState(moduleData.selection.details?.trainingProvider ?? "");
  const [option, setOption] = React.useState(moduleData.selection.details?.option ?? "");
  const [isRepeater, setIsRepeater] = React.useState(moduleData.selection.details?.isRepeater);
  const [willProvideDocumentsLater, setWillProvideDocumentsLater] = React.useState(
    moduleData.selection.details?.willProvideDocumentsLater
  );

  const { data: trainingProviders } = useGetAllTrainingProvidersQuery();

  const intl = useIntl();
  const classes = useStyles();

  const onIsRepeaterChange = (value: string | number) => {
    const isRepeater = value === "" ? undefined : value === 1 ? true : false;
    setIsRepeater(isRepeater);
  };

  const getIsRepeaterSelectValue = () => {
    if (isRepeater === undefined) {
      return "";
    }
    if (isRepeater === false) {
      return 0;
    }
    return 1;
  };

  const canSave = () => {
    return (
      trainingProvider &&
      trainingProvider !== "" &&
      (!moduleData.reference.needsOption || (option && option !== "")) &&
      isRepeater !== undefined &&
      (!moduleData.reference.needsDocuments || moduleData.attachments.length > 0 || willProvideDocumentsLater)
    );
  };

  return (
    <Dialog fullWidth={true} maxWidth="lg" open={!!moduleData} onClose={handleCancel} disableBackdropClick={true}>
      <DialogContent style={{ overflow: "hidden" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography>
              <TranslatedMessage id="startCertification.modulesDetailsStep.dialogHelperText" />
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Table size="small" className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell align="left">
                    <TranslatedMessage id="application.examLocationAndDate.moduleColumn" />
                  </TableCell>
                  <TableCell align="left">{GetTranslatedValueIntl(intl, moduleData.reference.name)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <TranslatedMessage id="application.moduleSelector.typeColumn" />
                  </TableCell>
                  <TableCell align="left">
                    {GetTranslation(intl, ModuleTypeTranslationKey[moduleData.reference.moduleType])}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <TranslatedMessage id="application.userSelector.examSession" />
                  </TableCell>
                  <TableCell align="left">{moduleData.examSessionName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <TranslatedMessage id="course.header" />
                  </TableCell>
                  <TableCell align="left">{moduleData.courseName}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>
                  <TranslatedMessage id="startCertification.modulesDetailsStep.trainingProvider" />
                </InputLabel>
                <Select
                  value={trainingProvider}
                  onChange={(event) => setTrainingProvider(event.target.value as string)}
                >
                  {trainingProviders &&
                    trainingProviders.map((p) => (
                      <MenuItem key={p.key} value={p.key}>
                        {GetTranslatedValueIntl(intl, p.label)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            {moduleData.reference.needsOption && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>
                    <TranslatedMessage id="startCertification.modulesDetailsStep.option" />
                  </InputLabel>
                  <Select value={option} onChange={(event) => setOption(event.target.value as string)}>
                    <MenuItem value={OralExamOption.KMU}>
                      <TranslatedMessage id="startCertification.modulesDetailsStep.option.kmu" />
                    </MenuItem>
                    <MenuItem value={OralExamOption.Privathaushalte}>
                      <TranslatedMessage id="startCertification.modulesDetailsStep.option.privathaushalte" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>
                  <TranslatedMessage id="startCertification.modulesDetailsStep.isRepeater" />
                </InputLabel>
                <Select
                  value={getIsRepeaterSelectValue()}
                  onChange={(event) => onIsRepeaterChange(event.target.value as string | number)}
                >
                  <MenuItem value={1}>
                    <TranslatedMessage id="common.yes" />
                  </MenuItem>
                  <MenuItem value={0}>
                    <TranslatedMessage id="common.no" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {moduleData.reference.needsDocuments && (
            <ModuleAttachments
              moduleId={moduleData.selection.moduleId}
              attachments={moduleData.attachments}
              willProvideDocumentsLater={willProvideDocumentsLater}
              setWillProvideDocumentsLater={setWillProvideDocumentsLater}
            />
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="contained" color="secondary">
          <TranslatedMessage id="common.cancel" />
        </Button>
        <Button
          onClick={() => handleOk({ trainingProvider, option, isRepeater, willProvideDocumentsLater })}
          variant="contained"
          color="primary"
          disabled={!canSave()}
        >
          <TranslatedMessage id="common.accept" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleDetailsDialog;
